import { Injectable } from '@angular/core';

/**
 * Service for date formatting utilities
 *
 * Provides methods for formatting dates in various formats required by
 * the application, including HubSpot integrations and PDF generation.
 *
 * @example
 * constructor(private dateFormatter: DateFormatterService) {}
 *
 * const midnightDate = this.dateFormatter.formatToMidnightISO(new Date());
 */
@Injectable()
export class DateFormatterService {

  constructor() { }

  /**
   * Formats a date to midnight (00:00:00) in ISO format
   * Used primarily for HubSpot date fields that require midnight timestamps
   *
   * @param date - JavaScript Date object or timestamp
   * @returns ISO string with time set to 00:00:00
   *
   * @example
   * formatToMidnightISO(new Date('2025-10-23T14:30:00'))
   * // Returns: '2025-10-23T00:00:00.000Z' (formatted for midnight)
   */
  formatToMidnightISO(date: Date | number): string {
    const dateObj = typeof date === 'number' ? new Date(date) : date;
    const dateCopy = new Date(dateObj);

    dateCopy.setHours(0);
    dateCopy.setMinutes(0);
    dateCopy.setSeconds(0);

    const isoString = dateCopy.toISOString();

    // Force midnight format: Replace time portion with T00:00:00
    const forcedMidnight = isoString.replace(
      isoString.substring(isoString.indexOf('T'), isoString.lastIndexOf(':00:')),
      'T00'
    );

    return forcedMidnight;
  }

  /**
   * Formats service dates (inicio and fin) to midnight ISO format
   *
   * @param servicio - Service object containing inicioDeServicio and finDeServicio
   * @returns Object with formatted start and end dates
   *
   * @example
   * formatServiceDates({ inicioDeServicio: 1698048000000, finDeServicio: 1698134400000 })
   * // Returns: {
   * //   forcedMidnighInicioDeServicioFecha: '2023-10-23T00:00:00.000Z',
   * //   forcedMidnighFinDeServicioFecha: '2023-10-24T00:00:00.000Z'
   * // }
   */
  formatServiceDates(servicio: any): {
    forcedMidnighInicioDeServicioFecha: string,
    forcedMidnighFinDeServicioFecha: string
  } {
    return {
      forcedMidnighInicioDeServicioFecha: this.formatToMidnightISO(servicio.inicioDeServicio),
      forcedMidnighFinDeServicioFecha: this.formatToMidnightISO(servicio.finDeServicio)
    };
  }

  /**
   * Formats dates from legacy confirmacion/cotizacion structure
   * Used for backward compatibility with old data structures
   *
   * @param servicio - Service object from confirmacion.servicios
   * @returns Object with formatted start and end dates
   */
  formatLegacyServiceDates(servicio: any): {
    forcedMidnighInicioDeServicioFecha: string,
    forcedMidnighFinDeServicioFecha: string
  } {
    const itinerario = servicio.itinerario || servicio;

    return {
      forcedMidnighInicioDeServicioFecha: this.formatToMidnightISO(itinerario.inicioDeServicio),
      forcedMidnighFinDeServicioFecha: this.formatToMidnightISO(itinerario.finDeServicio)
    };
  }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/date-formatter.service.ts